import {EmptyPolicyNumber, PolicyNumber, ValidPolicyNumber} from 'ApplicationEntry/models/EnteredPolicyNumber';
import {EmptyString, isNonEmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {
  isLoaded as isLoadedPackages,
  PackageModel,
  UnassignedPackageModel,
} from 'ApplicationEntry/models/ProductsRiders/PackageModel';
import * as I from 'immutable';
import * as RI from '../RiderInformation';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {
  isLoaded,
  SalaryDeductionOccupationClassInfo,
  SalaryDeductionOccupationClassModel,
  UnassignedSalaryDeductionOccupationClassModel,
} from '../SalaryDeductionOccupationClassModel';
import {Async, Loaded} from 'Core/models/Async';

const riderCodes: RI.GroupRiderCode[] = [
  {
    code: 'GWCADB',
    displayName: '',
    units: [],
    propertyName: 'accidentalDeathBenefit',
  },
  {
    code: 'GWCCT',
    displayName: '',
    units: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20],
    propertyName: '',
  },
  {
    code: 'GWCST',
    displayName: '',
    units: [],
    propertyName: '',
  },
  {
    code: 'GWCTYT',
    displayName: '',
    units: [10, 20, 30, 40, 50, 60, 70, 80, 90, 100],
    propertyName: '',
  },
  {
    code: 'GWCLTC',
    displayName: '',
    units: [],
    propertyName: '',
  },
  {
    code: 'GWCLTCE',
    displayName: '',
    units: [],
    propertyName: '',
  },
  {
    code: 'GWCLTCR',
    displayName: '',
    units: [],
    propertyName: '',
  },
  {
    code: 'GWCLTCRE',
    displayName: '',
    units: [],
    propertyName: '',
  },
  {
    code: 'GWCCI',
    displayName: '',
    units: [50, 100],
    propertyName: '',
  },
  {
    code: 'GWCTI',
    displayName: '',
    units: [],
    propertyName: '',
    controlType: RI.ControlType.Boolean,
  },
  {
    code: 'GWCWP',
    displayName: '',
    units: [],
    propertyName: '',
    controlType: RI.ControlType.Boolean,
  },
];

type IWLProps = {
  plan: StringUnion;
  situsState: StringUnion;
  type: StringUnion;
  faceAmt: StringUnion;
  policyNumber: PolicyNumber;
  priInsured: StringUnion;
  spouseInsured: StringUnion;
  replacement: boolean;
  riderInformation: RI.default;
  premium: StringUnion;
  calculatedPremium: StringUnion;
  premiumType: StringUnion;
  cwa: StringUnion;
  autoIssueEligibility: boolean;
  coverageLevel: StringUnion;
  occClass: SalaryDeductionOccupationClassModel;
  packageList: PackageModel;
  commission: StringUnion;
  deathBenefit: StringUnion;
  adb: boolean;
  sbVolume: StringUnion;
  premiumWaiver: boolean;
  product: StringUnion;
  jstamp: boolean;
  packageName: StringUnion;
  accidentalDeathBenefitTerminalIllness: boolean;
};

export enum GroupIWLType {
  accidentalDeathBenefit = 'GWCADB',
  premiumWaiver = 'GWCWP',
  spouseTermRider = 'GWCST',
  longTermCareAccelwithExtension = 'GWCLTCE',
  longTermCareAccelwithRestoration = 'GWCLTCR',
  longTermCareAccelwithRestAndExt = 'GWCLTCRE',
  chronicIllnessAccel50 = 'GWLCCI50',
  chronicIllnessAccel100 = 'GWLCCI100',
  accidentalDeathBenefitTerminalIllness = 'GWCTI',
  childrenTerm = 'GWCCTI',
  levelTerm20yr = 'GWCTYT',
  longTermAccel = 'GWCLTC',
}
export class InvalidGroupIWL {
  public readonly key = 'InvalidGroupIWL';

  constructor(
    public readonly plan: StringUnion,
    public readonly situsState: StringUnion,
    public readonly type: StringUnion,
    public readonly faceAmt: StringUnion,
    public readonly policyNumber: PolicyNumber,
    public readonly priInsured: StringUnion,
    public readonly spouseInsured: StringUnion,
    public readonly replacement: boolean,
    public readonly riderInformation: RI.default,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly premiumType: StringUnion,
    public readonly cwa: StringUnion,
    public readonly autoIssueEligibility: boolean,
    public readonly coverageLevel: StringUnion,
    public readonly occClass: SalaryDeductionOccupationClassModel,
    public readonly packageList: PackageModel,
    public readonly commission: StringUnion,
    public readonly deathBenefit: StringUnion,
    public readonly adb: boolean,
    public readonly sbVolume: StringUnion,
    public readonly premiumWaiver: boolean,
    public readonly product: StringUnion,
    public readonly jstamp: boolean,
    public readonly packageName: StringUnion,
    public readonly accidentalDeathBenefitTerminalIllness: boolean
  ) {}
}

export class ValidGroupIWL {
  public static createApp({
    plan,
    situsState,
    type,
    faceAmt,
    policyNumber,
    priInsured,
    spouseInsured,
    replacement,
    riderInformation,
    premium,
    calculatedPremium,
    premiumType,
    cwa,
    autoIssueEligibility,
    coverageLevel,
    occClass,
    packageList,
    commission,
    deathBenefit,
    adb,
    sbVolume,
    premiumWaiver,
    product,
    jstamp,
    packageName,
    accidentalDeathBenefitTerminalIllness,
  }: IWLProps) {
    return this.create(
      plan,
      situsState,
      type,
      faceAmt,
      policyNumber,
      priInsured,
      spouseInsured,
      replacement,
      riderInformation,
      premium,
      calculatedPremium,
      premiumType,
      cwa,
      autoIssueEligibility,
      coverageLevel,
      occClass,
      packageList,
      commission,
      deathBenefit,
      adb,
      sbVolume,
      premiumWaiver,
      product,
      jstamp,
      packageName,
      accidentalDeathBenefitTerminalIllness
    );
  }

  public static create(
    plan: StringUnion,
    situsState: StringUnion,
    type: StringUnion,
    faceAmt: StringUnion,
    policyNumber: PolicyNumber,
    priInsured: StringUnion,
    spouseInsured: StringUnion,
    replacement: boolean,
    riderInformation: RI.default,
    premium: StringUnion,
    calculatedPremium: StringUnion,
    premiumType: StringUnion,
    cwa: StringUnion,
    autoIssueEligibility: boolean,
    coverageLevel: StringUnion,
    occClass: SalaryDeductionOccupationClassModel,
    packageList: PackageModel,
    commission: StringUnion,
    deathBenefit: StringUnion,
    adb: boolean,
    sbVolume: StringUnion,
    premiumWaiver: boolean,
    product: StringUnion,
    jstamp: boolean,
    packageName: StringUnion,
    accidentalDeathBenefitTerminalIllness: boolean
  ) {
    return isNonEmptyString(plan) && isLoaded(occClass) && isLoadedPackages(packageList)
      ? new ValidGroupIWL(
          plan,
          situsState,
          type,
          faceAmt,
          policyNumber,
          priInsured,
          spouseInsured,
          replacement,
          riderInformation,
          premium,
          calculatedPremium,
          premiumType,
          cwa,
          autoIssueEligibility,
          coverageLevel,
          occClass,
          packageList,
          commission,
          deathBenefit,
          adb,
          sbVolume,
          premiumWaiver,
          product,
          jstamp,
          packageName,
          accidentalDeathBenefitTerminalIllness
        )
      : new InvalidGroupIWL(
          plan,
          situsState,
          type,
          faceAmt,
          policyNumber,
          priInsured,
          spouseInsured,
          replacement,
          riderInformation,
          premium,
          calculatedPremium,
          premiumType,
          cwa,
          autoIssueEligibility,
          coverageLevel,
          occClass,
          packageList,
          commission,
          deathBenefit,
          adb,
          sbVolume,
          premiumWaiver,
          product,
          jstamp,
          packageName,
          accidentalDeathBenefitTerminalIllness
        );
  }

  public readonly key = 'ValidGroupIWL';
  constructor(
    public readonly plan: StringUnion,
    public readonly situsState: StringUnion,
    public readonly type: StringUnion,
    public readonly faceAmt: StringUnion,
    public readonly policyNumber: PolicyNumber,
    public readonly priInsured: StringUnion,
    public readonly spouseInsured: StringUnion,
    public readonly replacement: boolean,
    public readonly riderInformation: RI.default,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly premiumType: StringUnion,
    public readonly cwa: StringUnion,
    public readonly autoIssueEligibility: boolean,
    public readonly coverageLevel: StringUnion,
    public readonly occClass: Loaded<SalaryDeductionOccupationClassInfo>,
    public readonly packageList: Loaded<object[]>,
    public readonly commission: StringUnion,
    public readonly deathBenefit: StringUnion,
    public readonly adb: boolean,
    public readonly sbVolume: StringUnion,
    public readonly premiumWaiver: boolean,
    public readonly product: StringUnion,
    public readonly jstamp: boolean,
    public readonly packageName: StringUnion,
    public readonly accidentalDeathBenefitTerminalIllness: boolean
  ) {}
}

export type GroupIWL = InvalidGroupIWL | ValidGroupIWL;

export type GroupIWLOptions<TReturn> = ToPartialMatchOptions<GroupIWL, 'key', TReturn>;

export const matchGroupIWL = <TReturn>(options: GroupIWLOptions<TReturn>) => (app: GroupIWL): TReturn => {
  switch (app.key) {
    case 'InvalidGroupIWL':
      return options.InvalidGroupIWL ? options.InvalidGroupIWL(app) : options.default(app);
    case 'ValidGroupIWL':
      return options.ValidGroupIWL ? options.ValidGroupIWL(app) : options.default(app);
  }
};

export const EmptyGroupIWL = new InvalidGroupIWL(
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  EmptyPolicyNumber,
  new EmptyString(),
  new EmptyString(),
  false,
  new RI.default(I.List<string>(), I.List<number>(), riderCodes),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  false,
  new EmptyString(),
  new UnassignedSalaryDeductionOccupationClassModel(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  false,
  new EmptyString(),
  false,
  new EmptyString(),
  false,
  new EmptyString(),
  false
);

export const changePlan = (plan: string) => (app: GroupIWL) =>
  ValidGroupIWL.createApp({...app, plan: NonEmptyString.create(plan)});

export const changeCoverageLevel = (coverageLevel: string) => (app: GroupIWL) =>
  ValidGroupIWL.createApp({...app, coverageLevel: NonEmptyString.create(coverageLevel)});
export const changeSitusState = (situsState: string) => (app: GroupIWL) =>
  ValidGroupIWL.createApp({...app, situsState: NonEmptyString.create(situsState)});
export const changeType = (type: string) => (app: GroupIWL) =>
  ValidGroupIWL.createApp({...app, type: NonEmptyString.create(type)});

export const changeFaceAmt = (faceAmt: string) => (app: GroupIWL) =>
  ValidGroupIWL.createApp({...app, faceAmt: NonEmptyString.create(faceAmt)});
export const changePolicyNumber = (polNum: string) => (app: GroupIWL) =>
  ValidGroupIWL.createApp({...app, policyNumber: ValidPolicyNumber.create(NonEmptyString.create(polNum))});
export const changePriInsured = (priInsured: string) => (app: GroupIWL) =>
  ValidGroupIWL.createApp({...app, priInsured: NonEmptyString.create(priInsured)});
export const changeSpouseInsured = (spouseInsured: string) => (app: GroupIWL) =>
  ValidGroupIWL.createApp({...app, spouseInsured: NonEmptyString.create(spouseInsured)});
export const changeReplacement = (replacement: boolean) => (app: GroupIWL) =>
  ValidGroupIWL.createApp({...app, replacement});

export const changeOccClass = (occClass: SalaryDeductionOccupationClassModel) => (app: GroupIWL) =>
  ValidGroupIWL.createApp({...app, occClass});

export const removeRider = (index: number) => (app: GroupIWL) => {
  const existingListAmounts: I.List<number> = app.riderInformation.amounts;
  const newListAmounts = existingListAmounts.delete(index);
  const existingListCodes: I.List<string> = app.riderInformation.codes;
  const newListCodes = existingListCodes.delete(index);
  const riderCodes: RI.RiderCode[] = app.riderInformation.riderData;

  return ValidGroupIWL.createApp({
    ...app,
    riderInformation: new RI.default(newListCodes, newListAmounts, riderCodes),
  });
};
export const changeRiderInformation = (riderAmount: number, riderCode: string, index: number) => (app: GroupIWL) => {
  const riderInformation = RI.default.changeRiderInformation(
    riderAmount,
    riderCode,
    index,
    app.riderInformation.amounts,
    app.riderInformation.codes
  );

  return ValidGroupIWL.createApp({
    ...app,
    riderInformation: new RI.default(riderInformation.riderCodes, riderInformation.riderAmounts, riderCodes),
  });
};

export const changePremium = (premium: string) => (app: GroupIWL) =>
  ValidGroupIWL.createApp({...app, premium: NonEmptyString.create(premium)});
export const changeCalculatedPremium = (calculatedPremium: string) => (app: GroupIWL) =>
  ValidGroupIWL.createApp({...app, calculatedPremium: NonEmptyString.create(calculatedPremium)});
export const changeCWA = (cwa: string) => (app: GroupIWL) =>
  ValidGroupIWL.createApp({...app, cwa: NonEmptyString.create(cwa)});
export const changePremiumType = (preType: string) => (app: GroupIWL) =>
  ValidGroupIWL.createApp({...app, premiumType: NonEmptyString.create(preType)});
export const changeAutoIssue = (autoIssueEligibility: boolean) => (app: GroupIWL) =>
  ValidGroupIWL.createApp({...app, autoIssueEligibility});

export const changePackageList = (packageList: Async<object[]>) => (app: GroupIWL) =>
  ValidGroupIWL.createApp({...app, packageList});

export const changePackageName = (packageName: string) => (app: GroupIWL) =>
  ValidGroupIWL.createApp({...app, packageName: NonEmptyString.create(packageName)});

export const changePackageItem = (packageItem: object) => (app: GroupIWL) =>
  ValidGroupIWL.createApp({
    ...app,
    accidentalDeathBenefitTerminalIllness:
      packageItem['accidentalDeathBenefitTerminalIllness'] !== null &&
      Number(packageItem['accidentalDeathBenefitTerminalIllness']) === 0,
    premiumWaiver: packageItem['premiumWaiver'] !== null && Number(packageItem['premiumWaiver']) === 0,
    adb: packageItem['accidentalDeathBenefit'] !== null && Number(packageItem['accidentalDeathBenefit']) === 0,
  });
export const changeWaiver = (premiumWaiver: boolean) => (app: GroupIWL) =>
  ValidGroupIWL.createApp({...app, premiumWaiver});

export const changeAdbTerminal = (accidentalDeathBenefitTerminalIllness: boolean) => (app: GroupIWL) =>
  ValidGroupIWL.createApp({...app, accidentalDeathBenefitTerminalIllness});
